import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { UserX, FileWarning, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ujmsFtaReport, ujmsCitationFtaSweep } from "../../services/ujmsApi";
import { Panel, Badge, Stat, thCls, tdCls, fmtDT, btnGhost, can } from "../ui";

export default function FtaReport() {
  const { access } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const load = () => ujmsFtaReport().then((r) => setData(r.data)).catch(() => setData({ rows: [], total: 0 }));
  useEffect(() => { load(); }, []);
  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await ujmsCitationFtaSweep();
      toast.success(r.data.flagged ? `${r.data.flagged} citation(s) flagged for failure to appear — judge notified` : "No new citation failures to appear");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sweep failed"); }
    setSweeping(false);
  };
  if (!data) return <div className="text-sm text-slate-400 p-8">Building FTA report…</div>;
  return (
    <div className="space-y-4" data-testid="fta-report-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <UserX className="w-6 h-6 text-red-600" /> Failure-to-Appear Report
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Defendants recorded as failed to appear at a hearing, with their open warrants — for the bench and the clerk's office.
            Must-appear citations are auto-flagged nightly at 2:00 AM when the court date passes with no appearance.
          </p>
        </div>
        {can(access, "administration.manage", "order.sign") && (
          <button className={`${btnGhost} no-print shrink-0`} onClick={sweep} disabled={sweeping} data-testid="fta-sweep-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${sweeping ? "animate-spin" : ""}`} /> Run Citation FTA Sweep Now
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
        <Stat label="FTA Defendants" value={data.total} testId="fta-total" />
        <Stat label="With Open Warrant" value={data.with_open_warrant} testId="fta-with-warrant" tone="text-red-700" />
        <Stat label="No Warrant Yet" value={data.without_warrant} testId="fta-without-warrant" tone="text-amber-700" />
      </div>
      <Panel title="failure to appear — open matters" testId="fta-table-panel">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Case</th><th className={thCls}>Defendant</th><th className={thCls}>Last FTA</th>
            <th className={thCls}>Days</th><th className={thCls}>FTAs</th><th className={thCls}>Open Warrants</th>
            <th className={thCls}>Custody</th><th className={thCls}>Case Status</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.case_id} className="hover:bg-slate-50" data-testid={`fta-row-${r.case_id}`}>
                <td className={tdCls}>
                  <Link to={`/ujms/cases/${r.case_id}`} className="font-mono text-xs text-ujms underline font-semibold">{r.case_number}</Link>
                  <div className="text-xs text-slate-400 line-clamp-1 max-w-[220px]">{r.caption}</div>
                </td>
                <td className={`${tdCls} font-semibold`}>{r.defendant || "—"}</td>
                <td className={`${tdCls} text-xs`}><span className="font-mono">{fmtDT(r.last_fta_at)}</span><div className="text-slate-400">{r.hearing_type}</div></td>
                <td className={`${tdCls} font-mono font-bold ${r.days_since_fta > 30 ? "text-red-700" : ""}`}>{r.days_since_fta ?? "—"}</td>
                <td className={`${tdCls} font-mono`}>{r.fta_count}</td>
                <td className={tdCls}>
                  {r.open_warrants.length ? r.open_warrants.map((w) => (
                    <span key={w.id} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-red-700 text-white rounded-sm px-1.5 py-0.5 mr-1 mb-0.5" data-testid={`fta-warrant-${w.id}`}>
                      <FileWarning className="w-3 h-3" /> {w.warrant_type}{w.bond_amount ? ` $${Number(w.bond_amount).toLocaleString()}` : ""}
                    </span>
                  )) : <span className="text-[10px] font-bold uppercase bg-amber-100 border border-amber-300 text-amber-800 rounded-sm px-1.5 py-0.5" data-testid={`fta-nowarrant-${r.case_id}`}>No open warrant</span>}
                </td>
                <td className={tdCls}>{r.in_custody ? <Badge value="IN_CUSTODY" /> : "—"}</td>
                <td className={tdCls}><Badge value={r.case_status} /></td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={8} className={`${tdCls} text-slate-400`}>No failure-to-appear defendants on record.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
